import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { useAddKadryOutreachCommentMutation, useMeQuery } from '@shared/api';
import { kadryDateTime, type KadryOutreach, type KadryOutreachComment } from '@entities/kadry';
import { KadryErrorBanner } from './KadryErrorBanner';

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const Head = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

const Feed = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  max-height: 220px;
  overflow-y: auto;
`;

const Bubble = styled.div<{ $own: boolean }>`
  align-self: ${({ $own }) => ($own ? 'flex-end' : 'flex-start')};
  max-width: 85%;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $own }) => ($own ? `${theme.colors.primary}1f` : theme.colors.surface)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
  line-height: 1.45;
`;

const Meta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

const Text = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Compose = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: flex-end;
`;

const Field = styled.textarea`
  flex: 1;
  min-width: 0;
  min-height: 40px;
  max-height: 120px;
  resize: vertical;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  line-height: 1.45;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const MAX_CHARS = 2000;

/** Кто и когда отметил «Связались». */
function contactedLine(o: KadryOutreach): string | null {
  if (!o.hr_contacted_at) return null;
  const who = o.hr_contacted_by ?? (o.followup_contacted ? 'кандидат подтвердил в боте' : null);
  return who ? `${who} · ${kadryDateTime(o.hr_contacted_at)}` : kadryDateTime(o.hr_contacted_at);
}

/**
 * Переписка кадровиков о кандидате, которому ушло предложение: у ответивших
 * «Подходит» — кто связался и что договорились, у остальных — пометки обзвона
 * («не берёт трубку», «перезвонить в пятницу»). Enter — отправить,
 * Shift+Enter — новая строка.
 */
export function KadryOutreachChat({
  outreach,
  comments,
  requestId,
  canWrite,
  autoFocus = false,
}: {
  outreach: KadryOutreach;
  comments: KadryOutreachComment[];
  requestId: string;
  canWrite: boolean;
  /** Поставить курсор в поле — чат открыли кнопкой «Добавить пометку». */
  autoFocus?: boolean;
}) {
  const me = useMeQuery().data?.user?.id;
  const [add, state] = useAddKadryOutreachCommentMutation();
  const [text, setText] = useState('');
  const feed = useRef<HTMLDivElement>(null);
  const contacted = contactedLine(outreach);
  const interested = outreach.response === 'interested';

  useEffect(() => {
    const el = feed.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [comments.length]);

  async function send() {
    const t = text.trim();
    if (!t || state.isLoading) return;
    try {
      await add({ outreachId: outreach.id, requestId, text: t }).unwrap();
      setText('');
    } catch {
      // Ошибка показывается под полем (state.error).
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  }

  return (
    <Box>
      <Head>
        {!interested ? (
          outreach.response === 'not_interested' ? (
            'Кандидат ответил «Не подходит»'
          ) : (
            'Кандидат ещё не ответил на предложение'
          )
        ) : contacted ? (
          <>
            Связался: <strong>{contacted}</strong>
          </>
        ) : (
          'С кандидатом ещё не связались'
        )}
      </Head>
      {comments.length > 0 ? (
        <Feed ref={feed}>
          {comments.map((c) => (
            <Bubble key={c.id} $own={c.author_id != null && c.author_id === me}>
              <Meta>
                <strong>{c.author_name ?? 'Сотрудник удалён'}</strong> · {kadryDateTime(c.created_at)}
              </Meta>
              <Text>{c.text}</Text>
            </Bubble>
          ))}
        </Feed>
      ) : (
        <Empty>
          Комментариев пока нет
          {canWrite
            ? interested
              ? ' — напишите, о чём договорились с кандидатом'
              : ' — например, «не берёт трубку, перезвонить в пятницу»'
            : ''}
          .
        </Empty>
      )}
      {canWrite && (
        <>
          <Compose>
            <Field
              rows={1}
              value={text}
              maxLength={MAX_CHARS}
              autoFocus={autoFocus}
              placeholder="Комментарий для коллег…"
              aria-label="Комментарий к кандидату"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKey}
            />
            <Button type="button" $size="sm" disabled={!text.trim() || state.isLoading} onClick={() => void send()}>
              {state.isLoading ? 'Отправляем…' : 'Отправить'}
            </Button>
          </Compose>
          {state.error ? <KadryErrorBanner error={state.error} onDismiss={() => state.reset()} /> : null}
        </>
      )}
    </Box>
  );
}
